import Message from "../../../pages/components/message";
import { MessageSections } from "../../../pages/components/message/message";
import { useAppSelector } from "../../../redux/store";
import DateFilter from "../../../pages/orders/my/tool-panel/filter/blocks/date-filter/date-filter";
import React, { useState } from "react";
import { DateObject } from "react-multi-date-picker";
import { BarekamSelect } from "../../../pages/components/select/select";
import {
  BarekamMenuItem,
  SelectedBarekamMenuItem,
} from "../../../pages/components/select/styled/orders";
import BarekamButton, { SimpleButtons } from "../../../pages/components/button";
import {
  BarekamIcon,
  IconVariants,
} from "../../../pages/components/icon/barekam-icon";
import BarekamText from "../../../pages/components/text";

const DashboardFilter = ({
  onFilterChange,
  filter,
  withCountries,
  withDate,
  withState,
}: ActionBarFilterProps) => {
  const { countries, orderStates } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const [contextFilter, setContextFilter] =
    useState<DashboardFilterModel>(filter);
  const { languageId } = useAppSelector((state) => state.messagesReducer);

  function handleDateFilterChange(dates: DateObject[]) {
    const filterClone = { ...contextFilter };

    if (dates.length === 1) {
      filterClone.minEnteredDate = dates[0].toDate().getTime();
      filterClone.maxEnteredDate = dates[0].add(1, "day").toDate().getTime();
    } else if (dates.length === 2) {
      filterClone.minEnteredDate = dates[0].toDate().getTime();
      filterClone.maxEnteredDate = dates[1].toDate().getTime();
    } else {
      delete filterClone.minEnteredDate;
      delete filterClone.maxEnteredDate;
    }

    setContextFilter(filterClone);
    onFilterChange(filterClone);
  }

  function handleCountryFilterChange(countryId: number) {
    let filterClone;

    if (countryId === 0 || !countryId) {
      filterClone = { ...contextFilter };
      delete filterClone.country;
    } else {
      filterClone = { ...contextFilter, country: countryId };
    }

    setContextFilter(filterClone);
    onFilterChange(filterClone);
  }

  function handleStateFilterChange(orderStateId: number) {
    let filterClone;

    if (orderStateId === 0 || !orderStateId) {
      filterClone = { ...contextFilter };
      delete filterClone.state;
    } else {
      filterClone = { ...contextFilter, state: orderStateId };
    }

    setContextFilter(filterClone);
    onFilterChange(filterClone);
  }

  function resetFilter() {
    setContextFilter({});
    onFilterChange({});
  }

  const FilterResetButton = () => {
    return (
      <BarekamButton type={SimpleButtons.LINK} onClick={() => resetFilter()}>
        <BarekamIcon variant={IconVariants.reset} />

        <BarekamText variant={"m"} bold color={"#A9A9C2"}>
          <Message messageKey={"reset"} sectionId={MessageSections.ORDERS} />
        </BarekamText>
      </BarekamButton>
    );
  };

  const CountrySelect = () => {
    return (
      <BarekamSelect
        variant={"outlined"}
        value={contextFilter.country || 0}
        onChange={(event) =>
          handleCountryFilterChange(event.target.value as number)
        }
        type={"standard"}
      >
        {!contextFilter.country ? (
          <SelectedBarekamMenuItem value={0} key={"undefined"}>
            <Message
              messageKey={"all_countries"}
              sectionId={MessageSections.ORDERS}
            />
          </SelectedBarekamMenuItem>
        ) : (
          <BarekamMenuItem value={0} key={"undefined"}>
            <Message
              messageKey={"all_countries"}
              sectionId={MessageSections.ORDERS}
            />
          </BarekamMenuItem>
        )}
        {countries.map((country) => {
          if (country.countryId === contextFilter.country) {
            return (
              <SelectedBarekamMenuItem
                value={country.countryId}
                key={country.name}
              >
                {country.name[languageId]}
              </SelectedBarekamMenuItem>
            );
          } else {
            return (
              <BarekamMenuItem value={country.countryId} key={country.name}>
                {country.name[languageId]}
              </BarekamMenuItem>
            );
          }
        })}
      </BarekamSelect>
    );
  };

  const StateSelect = () => {
    return (
      <BarekamSelect
        variant={"outlined"}
        value={contextFilter.state || 0}
        onChange={(event) =>
          handleStateFilterChange(event.target.value as number)
        }
        type={"standard"}
      >
        {!contextFilter.state ? (
          <SelectedBarekamMenuItem value={0} key={"undefined"}>
            <Message
              messageKey={"all_states"}
              sectionId={MessageSections.ORDERS}
            />
          </SelectedBarekamMenuItem>
        ) : (
          <BarekamMenuItem value={0} key={"undefined"}>
            <Message
              messageKey={"all_states"}
              sectionId={MessageSections.ORDERS}
            />
          </BarekamMenuItem>
        )}
        {orderStates.map((state) => {
          if (state.orderStateId === contextFilter.state) {
            return (
              <SelectedBarekamMenuItem
                value={state.orderStateId}
                key={state.name}
              >
                <Message
                  messageKey={state.name}
                  sectionId={MessageSections.ORDERS}
                />
              </SelectedBarekamMenuItem>
            );
          } else {
            return (
              <BarekamMenuItem value={state.orderStateId} key={state.name}>
                <Message
                  messageKey={state.name}
                  sectionId={MessageSections.ORDERS}
                />
              </BarekamMenuItem>
            );
          }
        })}
      </BarekamSelect>
    );
  };

  return (
    <>
      {withState ? <StateSelect /> : ""}

      {withCountries ? <CountrySelect /> : ""}

      {withDate ? (
        <DateFilter
          minEnteredDate={contextFilter.minEnteredDate}
          maxEnteredDate={contextFilter.maxEnteredDate}
          onFilterChange={handleDateFilterChange}
          noLabel
        />
      ) : (
        ""
      )}

      {Object.keys(contextFilter).length ? <FilterResetButton /> : ""}
    </>
  );
};

export interface DashboardFilterModel {
  minEnteredDate?: number;
  maxEnteredDate?: number;
  country?: number;
  state?: number;
}

interface ActionBarFilterProps {
  onFilterChange: (filter: DashboardFilterModel) => void;
  filter: DashboardFilterModel;
  withCountries?: boolean;
  withDate?: boolean;
  withState?: boolean;
}

export default DashboardFilter;
